import { addTicket, selectActiveTicket, updateTicket } from '@/redux/data/dataSlice';
import dayjs from 'dayjs';
import { cloneDeep, filter, findIndex, map, size } from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import {
  DiscountType,
  ItemType,
  ModifierOptionType,
  TicketItemType,
  Unit,
} from '../../redux/data/dataTypes';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';

type ticketType = 'PAY' | 'REFUND';
interface MyHookProps {
  type: ticketType;
}
const useTicket = (intialValue: MyHookProps = { type: 'PAY' }) => {
  const activeTicket = useAppSelector(selectActiveTicket);
  const dispatch = useAppDispatch();

  /**
   * Check if two items have the same unit configuration
   * Handles cases where unit properties might be undefined
   */
  const hasSameUnitConfig = (item1: TicketItemType, item2: TicketItemType): boolean => {
    // If both have no unit configuration, they're the same
    if (item1.unitName === undefined && item2.unitName === undefined) {
      return true;
    }

    // If only one has a unit configuration, they're different
    if (item1.unitName === undefined || item2.unitName === undefined) {
      return false;
    }

    // Both have unit configurations, compare all properties
    return (
      item1.unitName === item2.unitName &&
      (item1.unitPrice ?? null) === (item2.unitPrice ?? null) &&
      (item1.unitConversionFactor ?? null) === (item2.unitConversionFactor ?? null)
    );
  };

  const addItemToTicket = (item: ItemType, selectedUnit?: Unit) => {
    if (!item.price) return;

    // Determine the price to use based on priority:
    // 1. customerTierPriceAfterTax (if exists and customer is selected)
    // 2. priceAfterTax (if exists)
    // 3. price (fallback)
    const itemPrice = item.customerTierPriceAfterTax ?? item.priceAfterTax ?? item.price;

    const saleItem: TicketItemType = {
      ID: item.ID,
      name: item.name,
      nameAr: item.nameAr,
      SKU: item.SKU,
      sellingType: item.sellingType,
      price: itemPrice,
      cost: item.cost,
      quantity: item?.quantity ?? 1,

      unitPrice: selectedUnit?.price,
      unitName: selectedUnit?.name,
      unitNameAr: selectedUnit?.nameAr,
      unitConversionFactor: selectedUnit?.conversionFactor,
    };

    if (!activeTicket) {
      // Create new ticket with the item
      dispatch(
        addTicket({
          ID: uuidv4(),
          status: 'ACTIVE',
          category: 'DELIVERY',
          type: intialValue.type,
          createdAt: dayjs().toISOString(),
          items: [saleItem],
        })
      );
      return;
    }

    // Handle existing ticket
    const clonedTicket = cloneDeep(activeTicket);

    // Check if there's an existing item with the same ID and unit configuration
    const existingItemIndex =
      clonedTicket.items?.findIndex(
        (ticketItem) => ticketItem.ID === item.ID && hasSameUnitConfig(ticketItem, saleItem)
      ) ?? -1;

    if (existingItemIndex >= 0 && clonedTicket.items) {
      // Update quantity if same item with same unit configuration exists
      clonedTicket.items[existingItemIndex].quantity += item?.quantity ?? 1;
    } else {
      // Add as new line item if different unit configuration or first time
      clonedTicket.items = [...(clonedTicket.items ?? []), saleItem];
    }

    dispatch(updateTicket(clonedTicket));
  };

  const addItem = (item: ItemType, selectedUnit?: Unit) => {
    if (!item.price) return;

    // TODO: add this later depending on a prop on the item itself
    // if (item.stock != null && item.stock <= 0) {
    //   return new Promise((resolve, reject) => {
    //     Modal.confirm({
    //       title: t('common:negative_stock_alert_title'),
    //       content: t('common:negative_stock_alert_desc'),
    //       okText: t('common:continue'),
    //       cancelText: t('common:cancel'),
    //       centered: true,
    //       onOk: () => {
    //         addItemToTicket(item, selectedUnit);
    //         resolve(true);
    //       },
    //       onCancel: () => {
    //         reject('negative_stock');
    //       },
    //     });
    //   });
    // }
    addItemToTicket(item, selectedUnit);
  };

  const updateItem = (itemID: string, updateData: any) => {
    const clonedTicket = cloneDeep(activeTicket);
    const itemIdx = findIndex(clonedTicket?.items, (d) => d.ID == itemID);
    // const orginalItem = find(store.getState().data.items, (d) => d.ID == itemID);
    if (clonedTicket?.items && itemIdx > -1) {
      clonedTicket.items[itemIdx] = {
        ...clonedTicket?.items[itemIdx],
        ...updateData,
      };
      // TODO: add this later depending on a prop on the item itself
      // if (
      //   orginalItem &&
      //   orginalItem.stock != null &&
      //   orginalItem.stock - clonedTicket.items[itemIdx].quantity < 0
      // ) {
      //   return new Promise((resolve, reject) => {
      //     Modal.confirm({
      //       title: t('common:negative_stock_alert_title'),
      //       content: t('common:negative_stock_alert_desc'),
      //       okText: t('common:continue'),
      //       cancelText: t('common:cancel'),
      //       onOk: () => {
      //         dispatch(updateTicket(clonedTicket));
      //         resolve(true);
      //       },
      //       onCancel: () => {
      //         reject('negative_stock');
      //       },
      //     });
      //   });
      // }
      dispatch(updateTicket(clonedTicket));
    }
  };

  const updateModifierOption = (optionID: string, updateData: any) => {
    const clonedTicket = cloneDeep(activeTicket);
    const optionIdx = findIndex(clonedTicket?.modifierOptions, (d) => d.ID == optionID);
    if (clonedTicket?.modifierOptions && optionIdx > -1) {
      clonedTicket.modifierOptions[optionIdx] = {
        ...clonedTicket?.modifierOptions[optionIdx],
        ...updateData,
      };
      dispatch(updateTicket(clonedTicket));
    }
  };

  const addDiscount = (discount: DiscountType) => {
    return new Promise((resolve, reject) => {
      if (!discount?.value) return;
      if (!activeTicket) return reject('NO_ACTIVE_TICKET_DISCOUNT');
      // not items items exist
      const clonedTicket = cloneDeep(activeTicket);
      const checkItem = findIndex(clonedTicket.discounts, (d) => d?.ID == discount.ID);
      if (checkItem > -1 && clonedTicket.items) return reject('DISCOUNT_ALREADY_USED');

      clonedTicket.discounts = [
        ...(clonedTicket.discounts ?? []),
        {
          ID: discount.ID,
          value: discount.value,
          type: discount.type,
          name: discount.name,
        },
      ];
      dispatch(updateTicket(clonedTicket));
    });
  };
  const removeDiscount = (discount: DiscountType) => {
    return new Promise((resolve, reject) => {
      if (!discount?.ID) return;
      if (!activeTicket) return reject('NO_ACTIVE_TICKET');
      // not items items exist
      const clonedTicket = cloneDeep(activeTicket);
      clonedTicket.discounts = filter(clonedTicket.discounts, (d) => d.ID != discount?.ID);
      dispatch(updateTicket(clonedTicket));
    });
  };

  const addModifierOptions = (options: ModifierOptionType[]) => {
    if (!activeTicket) {
      dispatch(
        addTicket({
          ID: uuidv4(),
          status: 'ACTIVE',
          createdAt: dayjs().toISOString(),
          type: intialValue.type,
          modifierOptions: map(options, (d) => {
            return {
              ID: d.ID,
              name: d.name,
              nameAr: d.nameAr,
              price: d.price,
              quantity: 1,
            };
          }),
          category: 'DELIVERY',
        })
      );
    } else {
      const clonedTicket = cloneDeep(activeTicket);
      if (!clonedTicket.modifierOptions || size(clonedTicket.modifierOptions) == 0) {
        clonedTicket.modifierOptions = map(options, (d) => {
          return {
            ID: d.ID,
            name: d.name,
            nameAr: d.nameAr,
            price: d.price,
            quantity: 1,
          };
        });
      } else {
        options.forEach((option) => {
          const checkOptionsIndex = findIndex(
            clonedTicket.modifierOptions,
            (d) => d.ID == option.ID
          );
          if (checkOptionsIndex > -1 && clonedTicket.modifierOptions) {
            clonedTicket.modifierOptions[checkOptionsIndex].quantity += 1;
          } else {
            clonedTicket.modifierOptions = [
              ...(clonedTicket.modifierOptions ?? []),
              {
                ID: option.ID,
                name: option.name,
                nameAr: option.nameAr,
                price: option.price,
                quantity: 1,
              },
            ];
          }
        });
      }
      dispatch(updateTicket(clonedTicket));
    }
  };
  return {
    addItem,
    updateItem,
    updateModifierOption,
    addDiscount,
    removeDiscount,
    addModifierOptions,
  };
};

export default useTicket;
